import { configs, coreEngine, encrypt, nodes, store } from "@dinahmoe/dmafjs-core";
import { Group } from "./group.component.js";
class Container extends Group {
  namespace = "visual:container";

  init(cNode, event) {
    this.#setupScrollEventHandler(cNode);
    super.init(cNode, event);
  }

  addChild(cNode, config, event) {
    let childConfigInput = config.childConfig;

    if (!childConfigInput && config.templateUid) {
      childConfigInput = {
        namespace: "visual:template",
        templateUid: config.templateUid
      };
    }

    const childConfig = configs.resolveConfig(childConfigInput, event);

    const assignedUid = config.assignedUid;
    const resolvedChild = this._resolveTemplateRecursively(childConfig);

    if (!resolvedChild) {
      this.warn(cNode, `Child could not be resolved`);
      return;
    }

    const uid = assignedUid || resolvedChild.uid || encrypt.generateUid();

    if (nodes.getChildrenFor(cNode.uid).has(uid)) {
      this.warn(cNode, `${uid} was already in container ${cNode.uid}, ignoring`);
      return;
    }

    this._instantiateChild(cNode, config, event, resolvedChild);

    // Re-index if this container indexes ids
    if (cNode["indexIds"]) {
      this._indexIds(cNode);
    }
  }

  _instantiateChild(cNode, config, event, child) {
    const assignedUid = config.assignedUid ?? config.templateUid;

    coreEngine.create(
      {
        ...child,
        uid: assignedUid || child.uid,
        parentUid: cNode.uid,
      },
      event,
    );
  }

  _resolveTemplateRecursively(element, visitedTemplates = new Set()) {
    if (element.namespace === "visual:template") {
      const templateUid = element.templateUid;

      if (visitedTemplates.has(templateUid)) {
        console.error(`Circular template reference: ${templateUid}`);
        return null;
      }

      const template = store.get(`template.visual.${templateUid}`);

      if (!template) {
        console.warn(`Template ${templateUid} not found`);
        return null;
      }

      const visited = new Set(visitedTemplates);
      visited.add(templateUid);

      const resolved = this._resolveTemplateRecursively(template, visited);

      if (!resolved) return null;

      resolved.uid = templateUid;

      return resolved;
    }

    const resolved = { ...element };

    if (resolved.children && Array.isArray(resolved.children)) {
      resolved.children = resolved.children.map(childEntry => {
        return this._resolveTemplateRecursively(childEntry, visitedTemplates);
      }).filter(Boolean);
    }

    return resolved;
  }

  removeChild(cNode, config, event) {
    this._removeChild(cNode, config.childUid, event);
  }

  clear(cNode, config, event) {
    for (const child of nodes.getChildrenFor(cNode.uid).values())
      this._removeChild(cNode, child.uid, event);

    coreEngine.update({
      uid: cNode.uid,
      key: "scroll.x",
      value: 0
    });
    coreEngine.update({
      uid: cNode.uid,
      key: "scroll.y",
      value: 0
    });
  }

  receiveScrollEvent(cNode, message) {
    coreEngine.update({
      uid: cNode.uid,
      key: "scroll.x",
      value: message.value.scrollX,
      sender: message.sender
    });
    coreEngine.update({
      uid: cNode.uid,
      key: "scroll.y",
      value: message.value.scrollY,
      sender: message.sender
    });

    clearTimeout(cNode.scrollTimeout);
    cNode.scrollTimeout = setTimeout(scrollEnd, 200);
    cNode["scroll.isScrolling"] = true;

    function scrollEnd() {
      cNode["scroll.isScrolling"] = false;
    }
  }

  _removeChild(cNode, childUid, event) {
    if (!nodes.getChildrenFor(cNode.uid).has(childUid)) {
      this.log(cNode, `${childUid} was not in container`);
      return;
    }
    const node = nodes.getChildrenFor(cNode.uid).get(childUid);

    this.destroyHierarchy(node, event)
  }

  #setupScrollEventHandler(cNode) {
    if (
      cNode.props["container.overflow"] === "scroll" ||
      cNode.props["container.overflow"] === "scroll-x" ||
      cNode.props["container.overflow"] === "scroll-y"
    ) {
      if (!cNode.events) cNode.events = [];
      cNode.events.push({ type: "scroll" });
    }
  }

  receiveMessage(cNode, message) {
    if (message.key !== "scroll") {
      super.receiveMessage(cNode, message);
      return;
    }

    switch (message.key) {
      case "scroll": {
        this.receiveScrollEvent(cNode, message);
        break;
      }
    }
  }
}

const container = new Container();
export { Container, container };
